import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

import {
  APIgetGroup,
  APIcreateGroup,
  APIupdateGroup,
  APIdeleteGroup,
  APIupdateGroupCapabilities,
  APIupdateGroupMembers,
  APIgetGroupCapabilities,
} from '../../api/Group';
import { APIgetUsers } from '../../api/User';

import { showConfirmationModal } from '../../actions/App';

import AdminGroupEditHeader from '../../components/Admin/GroupEdit/Header';
import AdminGroupEditTabs from '../../components/Admin/GroupEdit/Tabs';
import AdminGroupEditTabContent from '../../components/Admin/GroupEdit/TabContent';

import { filterExcludingCapabilities } from '../../helpers/Auth';
import { addFlashMessage } from '../../helpers/FlashMessages';

import Loading from '../../components/Loading';
import ErrorBoundary from '../../components/ErrorBoundary';
import NothingFound from '../../components/NothingFound';
import GoBackToListButton from '../../components/GoBackToListButton';

class AdminGroupEdit extends Component {
  constructor(props) {
    super(props);

    this.state = {
      groupId: null,
      group: {
        roles: [],
      },
      users: [],
      capabilities: {},
      errors: [],
      activeTab: '1',
      is404: false,
      loading: true,
      saving: false,
      usersLoading: true,
      capabilitiesLoading: true,
      groupTouched: false,
      membersTouched: false,
      capabilitiesTouched: false,
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleSwitchTab = this.handleSwitchTab.bind(this);
    this.handleCapabilitySwitch = this.handleCapabilitySwitch.bind(this);
    this.handleAddMember = this.handleAddMember.bind(this);
    this.handleRemoveMember = this.handleRemoveMember.bind(this);
    this.handleChangeRole = this.handleChangeRole.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  componentDidMount() {
    this.loadGroup();
    this.loadUsers();
    this.loadCapabilities();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    const { groupId } = match.params;
    if (groupId && groupId !== prevProps.match.params.groupId) {
      this.loadGroup();
    }
  }

  loadGroup() {
    const { match } = this.props;
    const { groupId } = match.params;

    if (!groupId) {
      this.setState({
        group: {
          name: '',
          description: '',
          roles: [],
        },
        loading: false,
      });
      return;
    }

    this.setState({
      groupId,
      loading: true,
    });

    APIgetGroup(groupId).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({
          loading: false,
          group: data,
        });
      } else {
        this.setState({
          loading: false,
          is404: true,
        });
      }
    }).catch(() => {
      this.setState({
        loading: false,
        is404: true,
      });
    });
  }

  loadUsers() {
    this.setState({ usersLoading: true });

    APIgetUsers().then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ users: data });
      }
      this.setState({ usersLoading: false });
    });
  }

  loadCapabilities() {
    this.setState({ capabilitiesLoading: true });

    APIgetGroupCapabilities().then((response) => {
      const { data } = response;
      if (data && data) {
        this.setState({ capabilities: data });
      }
      this.setState({ capabilitiesLoading: false });
    });
  }

  resetState() {
    this.setState({
      saving: false,
      errors: [],
      groupTouched: false,
      capabilitiesTouched: false,
      membersTouched: false,
    });
  }

  handleError() {
    const {
      intl,
    } = this.props;
    this.resetState();
    addFlashMessage({
      type: 'danger',
      text: intl.formatMessage({ defaultMessage: 'Something went wrong! Please try again.' }),
    });
  }

  saveGroup() {
    const {
      groupId,
      group,
    } = this.state;
    const {
      intl,
      history,
    } = this.props;

    if (groupId) {
      return APIupdateGroup(group).catch(() => {
        this.handleError();
      });
    }

    return APIcreateGroup(group).then((response) => {
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'The new Team has been created' }),
      });
      const { data } = response;
      if (data && data.uuid) {
        history.push(`/admin/teams/${data.uuid}/edit`);
      }
    }).catch(() => {
      this.handleError();
    });
  }

  saveCapabilities() {
    const { group } = this.state;

    return APIupdateGroupCapabilities(group.uuid, group.capabilities).catch(() => {
      this.handleError();
    });
  }

  saveMembers() {
    const { group } = this.state;

    return APIupdateGroupMembers(group.uuid, group.users).catch(() => {
      this.handleError();
    });
  }

  handleSave() {
    const {
      intl,
    } = this.props;
    const {
      group,
      groupId,
      groupTouched,
      capabilitiesTouched,
      membersTouched,
    } = this.state;
    const promises = [];

    if (!group.name) {
      this.setState({
        errors: ['name'],
        activeTab: '1',
      });
      return;
    }

    if (groupTouched) {
      promises.push(this.saveGroup());
    }
    if (capabilitiesTouched) {
      promises.push(this.saveCapabilities());
    }
    if (membersTouched) {
      promises.push(this.saveMembers());
    }

    this.setState({
      saving: true,
      errors: [],
    });

    Promise.all(promises).then(() => {
      this.resetState();
      if (groupId) {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The changes have been saved' }),
        });
      }
    });
  }

  handleSwitchTab(tab) {
    this.setState({ activeTab: tab });
  }

  handleCapabilitySwitch(cap) {
    const { group } = this.state;

    const newGroup = { ...group };
    if (group.capabilities.includes(cap)) {
      newGroup.capabilities = newGroup.capabilities.filter(c => (c !== cap));
    } else {
      newGroup.capabilities.push(cap);
      newGroup.capabilities = filterExcludingCapabilities(cap, newGroup.capabilities);
    }

    this.setState({
      group: newGroup,
      timestamp: Date(),
      capabilitiesTouched: true,
    });
  }

  handleAddMember(userUuid) {
    const { group } = this.state;

    const newGroup = group;
    newGroup.users.push(userUuid);

    this.setState({
      group: newGroup,
      timestamp: Date(),
      membersTouched: true,
    });
  }

  handleRemoveMember(userUuid) {
    const { group } = this.state;

    const newGroup = group;
    newGroup.users = newGroup.users.filter(u => (u !== userUuid));

    this.setState({
      group: newGroup,
      timestamp: Date(),
      membersTouched: true,
    });
  }

  handleChangeRole(roleId) {
    const { group } = this.state;
    if (group.roles.find(r => (r.id === roleId))) {
      group.roles = group.roles.filter(r => (r.id !== roleId));
    } else {
      group.roles.push({ id: roleId });
    }
    this.setState({
      group,
      groupTouched: true,
    });
  }

  handleChange(e) {
    const {
      value,
      name,
      checked,
    } = e.target;
    const { group } = this.state;

    if (name === 'name' || name === 'description') {
      group[name] = value;
      this.setState({
        group,
        groupTouched: true,
      });
    }

    if (name === 'default_in_upload') {
      group[name] = checked;
      this.setState({
        group,
        groupTouched: true,
      });
    }
  }

  handleDelete() {
    const { group } = this.state;
    const {
      deleteGroup,
      history,
    } = this.props;

    deleteGroup(group, history);
  }

  render() {
    const {
      groupId,
      is404,
      loading,
    } = this.state;

    if (is404) {
      return (
        <NothingFound />
      );
    }

    return (
      <div className="main-content group-edit-content">
        {loading ? (
          <Loading />
        ) : (
          <ErrorBoundary>
            <GoBackToListButton
              scope="teams"
            />
            <AdminGroupEditHeader
              groupId={groupId}
            />
            <AdminGroupEditTabs
              {...this.state}
              handleSwitchTab={this.handleSwitchTab}
            />
            <ErrorBoundary>
              <AdminGroupEditTabContent
                {...this.state}
                handleChange={this.handleChange}
                handleCapabilitySwitch={this.handleCapabilitySwitch}
                handleAddMember={this.handleAddMember}
                handleRemoveMember={this.handleRemoveMember}
                handleChangeRole={this.handleChangeRole}
                handleSave={this.handleSave}
                handleDelete={this.handleDelete}
              />
            </ErrorBoundary>
          </ErrorBoundary>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, props) => ({
  deleteGroup: (group, history) => {
    const { intl } = props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Are you sure you want to delete this Team? This action can not be undone.' }),
      onConfirm: () => (
        APIdeleteGroup(group.uuid).then(() => {
          history.push('/admin/teams');
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  },
});

AdminGroupEdit.propTypes = {
  intl: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  deleteGroup: PropTypes.func.isRequired,
};

export default withRouter(injectIntl(connect(null, mapDispatchToProps)(AdminGroupEdit)));
